import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { Panel, PanelHeader, Skeleton, ReadFailed } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';

/**
 * The domains in a corpus, and the shapes inside each one.
 *
 * The connections graph draws "was found in" over and over: bucket, tool,
 * file. This draws what the data is. The backend groups every distinct record
 * shape into domains by the field names they share, so 71 shapes carrying
 * scripts, dependencies, license and main arrive as one domain of 64,389
 * files rather than as 71 unrelated piles.
 *
 * A domain is the unit worth reading, so it leads. Its shapes are underneath
 * for anyone who wants them. The joining keys are shown because they are the
 * evidence for the grouping - they say why these files are the same kind of
 * thing, and they were read out of the records rather than out of a filename.
 *
 * Some domains join on nothing and hold one shape. Those are singletons rather
 * than groups, and they are labelled that way instead of being padded out to
 * look like the rest.
 */

interface Domain {
  domainKey: string;
  joiningKeys: string[];
  toolIds: string[];
  signatureSha256s: string[];
  shapeCount: number;
  objectCount: number;
  unrecognisedObjectCount: number;
}

interface ShapeNode {
  signatureSha256: string;
  recordShape: string;
  signatureKeys: string[];
  objectCount: number;
  unrecognisedObjectCount: number;
  toolId?: string | null;
}

interface GraphData {
  domains: Domain[];
  nodes: ShapeNode[];
  edgeCount: number;
}

const SHAPE: Record<string, string> = {
  ARRAY_OF_OBJECTS: 'a list of records',
  SINGLE_OBJECT: 'one record per file',
  JSON_LINES: 'one record per line',
};

function read(value: unknown): GraphData | null {
  const data = (value as { data?: unknown })?.data ?? value;
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.domains) || !Array.isArray(d.nodes)) return null;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const num = (v: unknown) => (typeof v === 'number' ? v : 0);
  const arr = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
  return {
    domains: (d.domains as Record<string, unknown>[]).map((x) => ({
      domainKey: str(x.domainKey),
      joiningKeys: arr(x.joiningKeys),
      toolIds: arr(x.toolIds),
      signatureSha256s: arr(x.signatureSha256s),
      shapeCount: num(x.shapeCount),
      objectCount: num(x.objectCount),
      unrecognisedObjectCount: num(x.unrecognisedObjectCount),
    })),
    nodes: (d.nodes as Record<string, unknown>[]).map((x) => ({
      signatureSha256: str(x.signatureSha256),
      recordShape: str(x.recordShape),
      signatureKeys: arr(x.signatureKeys),
      objectCount: num(x.objectCount),
      unrecognisedObjectCount: num(x.unrecognisedObjectCount),
      toolId: typeof x.toolId === 'string' ? x.toolId : null,
    })),
    edgeCount: Array.isArray(d.edges) ? d.edges.length : 0,
  };
}

export function DomainGraph({ bucketId }: { bucketId: string }) {
  const q = useQuery({
    queryKey: ['domain-graph', bucketId],
    queryFn: ({ signal }) => backend.getDomainGraph(bucketId, signal),
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 120_000,
  });
  const [open, setOpen] = useState<string | null>(null);

  const data = useMemo(() => (q.data ? read(q.data) : null), [q.data]);
  const domains = useMemo(
    () => [...(data?.domains ?? [])].sort((a, b) => b.objectCount - a.objectCount),
    [data],
  );
  const byShape = useMemo(
    () => new Map((data?.nodes ?? []).map((n) => [n.signatureSha256, n])),
    [data],
  );

  const total = domains.reduce((n, d) => n + d.objectCount, 0);
  const biggest = Math.max(...domains.map((d) => d.objectCount), 1);
  const grouped = domains.filter((d) => d.shapeCount > 1).length;

  return (
    <Panel>
      <PanelHeader
        title="What this data is"
        sub={
          q.isPending
            ? 'grouping record shapes into domains by the fields they share'
            : domains.length === 0
              ? 'record shapes grouped by the fields they share'
              : `${domains.length} domains over ${total.toLocaleString()} files · ${grouped} of them join several shapes together`
        }
      />

      {q.isPending ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-7 w-full" />
          ))}
        </div>
      ) : q.isError ? (
        <ReadFailed
          onRetry={() => void q.refetch()}
          what={
            q.error instanceof ApiError && q.error.code === 'DEPENDENCY_UNAVAILABLE'
              ? 'The backend serves this but the store behind it is not available for this bucket yet.'
              : q.error instanceof ApiError
                ? (q.error.reason ?? q.error.message)
                : 'The API did not respond.'
          }
        />
      ) : domains.length === 0 ? (
        <p className="px-4 py-3 text-small text-ink-3">
          No record shapes were found here, so there is no domain structure to group. This fills in when a walk finds
          files holding structured records.
        </p>
      ) : (
        <>
          {domains.map((d) => {
            const singleton = d.shapeCount <= 1 || d.joiningKeys.length === 0;
            const isOpen = open === d.domainKey;
            return (
              <div key={d.domainKey} className="border-b border-line last:border-0">
                <button
                  onClick={() => setOpen(isOpen ? null : d.domainKey)}
                  className="block w-full px-4 py-3 text-left transition-colors duration-fast hover:bg-hov"
                  aria-expanded={isOpen}
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="mono text-row text-ink">{d.objectCount.toLocaleString()} files</span>
                    <span className="text-caption text-ink-3">
                      {singleton ? 'one shape on its own' : `${d.shapeCount} shapes that agree`}
                    </span>
                    <span className="ml-auto flex items-center gap-1.5">
                      {d.toolIds.slice(0, 4).map((t) => (
                        <ToolIcon key={t} tool={t} size={15} />
                      ))}
                      {d.toolIds.length === 0 && (
                        <span className="text-caption text-ink-4">no rule names it</span>
                      )}
                    </span>
                  </div>

                  <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
                    <span
                      className={cn('block h-full rounded-full', singleton ? 'bg-line-2' : 'bg-acc')}
                      style={{ width: `${Math.max((d.objectCount / biggest) * 100, 1.5)}%` }}
                    />
                  </span>

                  {d.joiningKeys.length > 0 && (
                    <span className="mt-2 flex flex-wrap items-center gap-1">
                      <span className="text-caption text-ink-4">joined by</span>
                      {d.joiningKeys.slice(0, 8).map((k) => (
                        <span
                          key={k}
                          className="mono rounded border border-line bg-panel-2 px-1.5 py-0.5 text-caption text-acc-2"
                        >
                          {k}
                        </span>
                      ))}
                      {d.joiningKeys.length > 8 && (
                        <span className="text-caption text-ink-4">and {d.joiningKeys.length - 8} more fields</span>
                      )}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-line bg-panel-2">
                    {d.signatureSha256s.map((sha) => {
                      const n = byShape.get(sha);
                      if (!n) return null;
                      return (
                        <div key={sha} className="flex flex-wrap items-baseline gap-x-3 border-b border-line px-4 py-2 last:border-0">
                          <span className="mono text-small text-ink">{n.objectCount.toLocaleString()} files</span>
                          <span className="text-caption text-ink-3">
                            {SHAPE[n.recordShape] ?? say(n.recordShape)}
                          </span>
                          {n.toolId && <ToolIcon tool={n.toolId} size={14} />}
                          <span className="mono w-full truncate text-caption text-ink-4" title={n.signatureKeys.join(', ')}>
                            {n.signatureKeys.slice(0, 14).join(' · ')}
                            {n.signatureKeys.length > 14 ? ` · +${n.signatureKeys.length - 14}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
            Every grouping here is a set of field names the records themselves carry, never a filename or a folder.
            {data ? ` ${data.nodes.length.toLocaleString()} distinct shapes, ${data.edgeCount.toLocaleString()} agreements between them.` : ''}{' '}
            A domain with no tool icon is one no rule describes, which is exactly the part of a corpus attribution
            cannot reach.
          </p>
        </>
      )}
    </Panel>
  );
}
